import { FC, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { FixedList, IInputField, ITextField, logger } from "cayo.ui";
import { normalizePropertyNames } from "@cayo/admin-components/components/grids/utils";
import { IObjectSelectionGrid } from "@cayo/shared/api/types/schema";
import { appSignals } from "@cayo/shared/components/App/utils";
import { withHidden } from "@cayo/shared/components/Hoc/withHidden";
import { commonMessages } from "@cayo/shared/messages/common";
import useService from "@cayo/shared/services/hooks/useService";
import { objectUtils } from "@cayo/shared/utils/object";
import { useObjectSelectionGrid } from "./ObjectSelectionGrid/useObjectSelectionGrid";

type ComputersGridProps = IObjectSelectionGrid & IInputField;

const ComputersGrid: FC<ComputersGridProps> = ({
  name,
  columns,
  value,
  label,
  setValue,
  noItemsText,
  idPropName,
  readOnly,
  modalOptions,
}) => {
  // store the initial items of the grid, which we compare with added and removed items, do not update
  const initialItems = useMemo(() => {
    if (value) {
      const items = Array.isArray(value) ? value : [value];
      const fixedItems = normalizePropertyNames(items);
      return fixedItems;
    }
    return [];
  }, [value]);

  const actionBuilder = useService("actionBuilder");
  const intl = useIntl();

  const { commands, items, onAddItems, isPickerVisible, hidePicker } = useObjectSelectionGrid(
    initialItems,
    readOnly,
    undefined,
    idPropName
  );

  const onSubmit = (object: any) => {
    logger.debug("Form onOk", object);

    onAddItems([object]);
    hidePicker();
    return Promise.resolve();
  };

  useEffect(() => {
    if (!objectUtils.areObjectsEqual(items, value)) {
      setValue(items);
    }
  }, [value, items.length]);

  useEffect(() => {
    if (isPickerVisible) {
      appSignals.send("showForm", {
        form: {
          title: modalOptions.title,
          actionBuilder,
          type: "json-form",
          items: [
            {
              label: modalOptions.description,
              required: true,
              name: "name",
              autoFocus: true,
              type: "text-field",
            } as ITextField,
          ],
          footer: {
            okText: intl.formatMessage(commonMessages.ok),
            cancelText: intl.formatMessage(commonMessages.cancel),
          },
          onSubmit,
          onClose: (result) => {
            logger.debug("Form onClose", result);
            hidePicker();
          },
        },
      });
    } else {
      hidePicker();
    }
  }, [isPickerVisible]);

  return (
    <FixedList
      name={name!}
      label={label}
      columns={columns}
      value={items}
      layoutMode="fixedColumns"
      showAdjustColumnsButton
      selectionMode="multiple"
      setValue={(v) => {
        setValue(v);
      }}
      commands={commands}
      noItemsText={items?.length ? undefined : noItemsText}
      readOnly={readOnly}
    />
  );
};

export default withHidden(ComputersGrid);
